import React, { useRef } from 'react'
import { Outlet, useOutletContext, useNavigate } from 'react-router-dom'
import { DataGrid } from 'vendor/x-data-grid'
import { useServerPagedDataTable } from 'hooks'

let rootFontSize = 12

function ListView(_props) {
  const {
    fixedParams = {},
    columns = [],
    filterBarPresets = [],
    filterPresetParamName = 'paramState',
    filterBarChildren = null,
    queryHook = null,
    linksComponent: LinksComponent = () => null,
    rowIdKey = 'id',
    checkboxSelection = true,
    rowClickHandler = null,
    filterCustomParams = {},
    selectedRowsAction = () => null,
    actions: Actions = React.Fragment,
    isRowSelectable = null,
    rowHeight = 3.3,
    headerHeight = null,
    selectAllQuery = null,
    selectAllQueryParams = {},
    selectAllTransformResponse = null,
  } = _props
  React.useEffect(() => {
    rootFontSize =
      parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          '--root-font-size'
        )
      ) || 12
  }, [])

  const navigate = useNavigate()
  const search = useOutletContext()?.search || _props?.search || ''
  const searchTramix =
    useOutletContext()?.searchTramix || _props?.searchTramix || ''
  const initialFilterCustomParamsRef = useRef(filterCustomParams)
  const initialFilterCustomParamsKeysRef = useRef(
    Object.keys(filterCustomParams)
  )
  const isFirstRender = useRef(true)
  const [
    dataGridProps,
    {
      query,
      FilterBarComponent,
      updateCustomParams,
      selectedRows,
      setCurrentPageRows,
    },
  ] = useServerPagedDataTable({
    rowIdKey: rowIdKey,
    filterBarPresets: filterBarPresets,
    filterPresetParamName: filterPresetParamName,
    customParamsInitialValues: initialFilterCustomParamsRef.current,
    search: search,
    searchTramix: searchTramix,
    selectAllQuery: selectAllQuery,
    selectAllQueryParams: selectAllQueryParams,
    fixedParams: { ...fixedParams },
    selectAllTransformResponse: selectAllTransformResponse,
  })

  const dataGridWrapperRef = useRef(null)

  const { data, isLoading, error } = queryHook(query, {
    refetchOnMountOrArgChange: 60,
  })

  React.useEffect(
    () => {
      if (!isFirstRender.current) {
        updateCustomParams({ ...filterCustomParams })
      } else {
        isFirstRender.current = false
      }
    },
    initialFilterCustomParamsKeysRef.current.map(
      (key) => filterCustomParams[key]
    )
  )

  React.useEffect(() => {
    if (data?.rows) {
      setCurrentPageRows(data.rows)
    }
  }, [data])

  const handleRowClick = React.useCallback((rowData) => {
    const { row = null } = rowData
    if (rowClickHandler) {
      rowClickHandler(row)
    } else {
      navigate(`${row[rowIdKey]}`)
    }
  }, [])

  let getRowId = useRef(rowIdKey ? (row) => row[rowIdKey] : null)

  return (
    <>
      <LinksComponent>{selectedRowsAction(selectedRows)}</LinksComponent>
      <Actions />
      <FilterBarComponent>{filterBarChildren}</FilterBarComponent>
      <div
        ref={dataGridWrapperRef}
        style={{
          width: '100%',
          height: `calc( 100vh - ${
            dataGridWrapperRef?.current?.offsetTop || 25 * 16
          }px - 2rem)`,
          minHeight: `${52 * 6}px`,
        }}
      >
        {dataGridWrapperRef?.current?.offsetTop && (
          <DataGrid
            {...dataGridProps}
            rows={data?.rows || []}
            columns={columns}
            rowCount={data?.count || 0}
            headerHeight={headerHeight || 56}
            loading={isLoading}
            getRowId={getRowId.current}
            onRowClick={(row) => handleRowClick(row)}
            rowHeight={
              rowHeight
                ? parseInt(rootFontSize * rowHeight)
                : parseInt(rootFontSize * 3.3)
            }
            checkboxSelection={
              isRowSelectable === null ? null : checkboxSelection
            }
            isRowSelectable={isRowSelectable}
          />
        )}
      </div>
      {error && <div>Error: {error.message}</div>}
      <Outlet
        context={{
          selectedRows,
          currentRows: data?.rows || [],
        }}
      />
    </>
  )
}

export default ListView
